import { useMemo } from 'react';
import { ColumnDef, createColumnHelper, SortingFn } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { SurveyTemplateDto, SurveyTemplateTranslateDto } from '@families-link/shared';
import { SurveyTemplatesTableHandlers } from '../../components/survey-templates-table/survey-templates-table.component';
import { SurveyTemplatesTableActions } from '../../components/survey-templates-table/survey-templates-table-actions.component';
import { Typography } from '@mui/material';
import { tableCellTypographyOverflowStyles } from '../../../_core/styles';
const helper = createColumnHelper<SurveyTemplateDto>();

export function useSurveyTemplatesTableColumns(
	handlers: SurveyTemplatesTableHandlers
): ColumnDef<SurveyTemplateDto, string>[] {
	const { onEdit, onView, onDelete, onAttachDistrict } = handlers;

	const [t] = useTranslation();

	const result = useMemo(
		() => [
			helper.accessor('title', {
				header: t('survey-templates.columns.name'),
				meta: {
					flex: 1,
				},
			}) as ColumnDef<SurveyTemplateDto, string>,
			helper.accessor('questionsCount', {
				header: t('survey-templates.columns.questions'),
				meta: {
					flex: 0.5,
				},
			}) as ColumnDef<SurveyTemplateDto, string>,
			helper.accessor('translates', {
				header: t('survey-templates.columns.translated-languages'),
				meta: {
					flex: 1,
				},
				sortDescFirst: false,
				cell: ({ row, column }) => {
					const languagesList = row.original.translates
						?.map((translate: SurveyTemplateTranslateDto) => translate.language)
						?.sort();
					if(column.getIsSorted() === 'desc' ){
						languagesList?.reverse();
					}
					return (
						<Typography sx={tableCellTypographyOverflowStyles} data-overflow="true">
							{languagesList?.length ? languagesList?.join(', ') : '-'}
						</Typography>
					);
				},
			}) as ColumnDef<SurveyTemplateDto, string>,
			helper.accessor('isDefaultTemplate', {
				header: t('survey-templates.columns.is-default'),
				meta: {
					flex: 1,
				},
				sortDescFirst: false,
				enableSorting: true,
				cell: ({ getValue }) => {
					const defaultLanguage = getValue();
					return (
						<Typography sx={tableCellTypographyOverflowStyles} data-overflow="true">
							{defaultLanguage ? 'Yes' : 'No'}
						</Typography>
					);
				},
			}) as ColumnDef<SurveyTemplateDto, string>,

			helper.display({
				id: 'actions',
				meta: {
					flex: 1,
				},
				cell: (props) => (
					<SurveyTemplatesTableActions
						surveyTemplate={props.row.original}
						adminTemplate={props.row.original.adminTemplate}
						onEdit={onEdit}
						onDelete={onDelete}
						onView={onView}
						onAttachDistrict={onAttachDistrict}
					/>
				),
			}) as ColumnDef<SurveyTemplateDto>,
		],
		[t, onEdit, onDelete, onView, onAttachDistrict]
	);

	return result;
}
